// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import { fetchAuthSession } from "aws-amplify/auth";
import { notifyProgressSaved } from "./progressEvents";
import { readDeviceProgress } from "./deviceProgress";
import { isDesktopLocalProfile } from "./desktopProfile";

const TABLE_NAME = process.env.REACT_APP_DYNAMODB_TABLE_NAME;

// Offline desktop build only: there is no cloud. The few single-row preferences that still go through this
// module (JLPT levels, country, currency, quiz stats) are kept in localStorage instead, and batch reads
// (recent activity) are answered from the device store, so no network call, SDK load or Cognito session
// (fetchAuthSession) ever happens. Every other function fails closed in loadModules().
const LOCAL_ROW_PREFIX = "nihongotech.localProfile.v1.";
function readLocalRow(userId, feature) {
    try {
        const row = JSON.parse(localStorage.getItem(LOCAL_ROW_PREFIX + feature) || "null");
        return row ? { userId, feature, data: row.data, updatedAt: row.updatedAt } : null;
    } catch {
        return null;
    }
}

// The DynamoDB SDK is only needed once a signed-in user's progress actually
// gets read from or written to the cloud - not for every anonymous visitor,
// who never calls getCloudProgress/putCloudProgress at all. Loading it
// dynamically keeps it out of the eager main bundle.
let modulesPromise = null;
let testModulesLoader = null;

// Keeps the SDK lazy in production while allowing tests to reset only this
// module's loader cache instead of resetting Jest's entire module registry.
export function __resetProgressCloudModulesForTests() {
    if (process.env.NODE_ENV === "test") modulesPromise = null;
}

export function __setProgressCloudModulesLoaderForTests(loader) {
    if (process.env.NODE_ENV === "test") {
        modulesPromise = null;
        testModulesLoader = loader;
    }
}

function loadModules() {
    if (isDesktopLocalProfile) return Promise.reject(new Error("Cloud sync is disabled in the offline desktop app."));
    if (!modulesPromise) {
        modulesPromise = (testModulesLoader ? Promise.resolve().then(testModulesLoader) : Promise.all([
            import("@aws-sdk/client-dynamodb"),
            import("@aws-sdk/lib-dynamodb"),
        ])
            .then(([{ DynamoDBClient }, { DynamoDBDocumentClient, GetCommand, PutCommand, BatchGetCommand }]) => {
                const client = new DynamoDBClient({
                    region: process.env.REACT_APP_AWS_REGION,
                    credentials: async () => {
                        const session = await fetchAuthSession();
                        return session.credentials;
                    },
                });
                return { docClient: DynamoDBDocumentClient.from(client), GetCommand, PutCommand, BatchGetCommand };
            }))
            .catch((err) => {
                // Don't let a transient failure (e.g. a network blip loading the SDK
                // chunks) permanently break cloud sync for the rest of the session -
                // let the next call retry from scratch.
                modulesPromise = null;
                throw err;
            });
    }
    return modulesPromise;
}

export async function getCloudProgress(userId, feature) {
    if (isDesktopLocalProfile) return readLocalRow(userId, feature);
    const { docClient, GetCommand } = await loadModules();
    const { Item } = await docClient.send(
        new GetCommand({ TableName: TABLE_NAME, Key: { userId, feature }, ConsistentRead: true })
    );
    return Item ?? null;
}

// Reads several features for one user in a single round trip, rather than
// N sequential getCloudProgress calls - used by the levels/XP aggregation,
// which needs about ten feature rows just to render a header badge.
// Incomplete responses must not masquerade as a lower level. The caller
// displays an error and offers a retry if DynamoDB leaves keys unprocessed.
export async function getCloudProgressBatch(userId, features) {
    if (!features?.length) return {};
    if (isDesktopLocalProfile) {
        const rows = await Promise.all(features.map((feature) => readDeviceProgress(userId, feature)));
        return Object.fromEntries(features.flatMap((feature, index) => rows[index].record
            ? [[feature, { userId, feature, data: rows[index].record.data, updatedAt: rows[index].record.updatedAt }]]
            : []));
    }
    const { docClient, BatchGetCommand } = await loadModules();
    const { Responses, UnprocessedKeys } = await docClient.send(
        new BatchGetCommand({
            RequestItems: {
                [TABLE_NAME]: { Keys: features.map((feature) => ({ userId, feature })), ConsistentRead: true },
            },
        })
    );
    if (UnprocessedKeys?.[TABLE_NAME]?.Keys?.length) {
        throw new Error("Progress read incomplete; please retry.");
    }
    const items = Responses?.[TABLE_NAME] ?? [];
    const byFeature: Record<string, { data: any }> = {};
    items.forEach((item) => {
        byFeature[item.feature] = item;
    });
    return byFeature;
}

export async function putCloudProgress(userId, feature, data) {
    const updatedAt = new Date().toISOString();
    if (isDesktopLocalProfile) {
        localStorage.setItem(LOCAL_ROW_PREFIX + feature, JSON.stringify({ data, updatedAt }));
        notifyProgressSaved({ userId, feature });
        return updatedAt;
    }
    const { docClient, PutCommand } = await loadModules();
    await docClient.send(
        new PutCommand({
            TableName: TABLE_NAME,
            Item: { userId, feature, data, updatedAt },
        })
    );
    notifyProgressSaved({ userId, feature });
    return updatedAt;
}

// Strong reads plus conditional writes protect reconciliation from another
// device winning between the read and write. No new IAM action is needed.
export async function getCloudProgressForSync(userId, feature) {
    const { docClient, GetCommand } = await loadModules();
    const { Item } = await docClient.send(new GetCommand({ TableName: TABLE_NAME, Key: { userId, feature }, ConsistentRead: true }));
    return Item ?? null;
}

export async function putCloudProgressConditional(userId, feature, data, previous, deviceId, revision, isActive) {
    const { docClient, PutCommand } = await loadModules();
    if (!isActive()) throw new Error("Account changed");
    const updatedAt = new Date().toISOString();
    const syncVersion = Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, "0")).join("");
    const names = { "#key": "userId" };
    const values = {};
    let condition = "attribute_not_exists(#key)";
    if (previous) {
        delete names["#key"];
        names["#version"] = "syncVersion";
        names["#updated"] = "updatedAt";
        condition = previous.syncVersion ? "#version = :version" : "attribute_not_exists(#version)";
        if (previous.syncVersion) values[":version"] = previous.syncVersion;
        condition += previous.updatedAt ? " AND #updated = :updated" : " AND attribute_not_exists(#updated)";
        if (previous.updatedAt) values[":updated"] = previous.updatedAt;
    }
    const item = { ...previous, userId, feature, data, updatedAt, syncVersion,
        syncReceipts: { ...(previous?.syncReceipts || {}), [deviceId]: revision } };
    await docClient.send(new PutCommand({ TableName: TABLE_NAME, Item: item, ConditionExpression: condition,
        ExpressionAttributeNames: names, ...(Object.keys(values).length ? { ExpressionAttributeValues: values } : {}) }));
    notifyProgressSaved({ userId, feature });
    return item;
}
